import { createAsyncThunk, createSlice, SerializedError, Draft } from '@reduxjs/toolkit';

import { AlgoliaTrail, TrailId } from '../../../types/trails';
import { AlertModel, AlertId, AlgoliaAlert } from '../../../types/alerts';
import { TrailsAlertResponse } from '../../../utils/requests/alertRequests';
import { getAlgoliaAlert } from '../../../utils/alertsHelper';
import { AlgoliaObjectType, GroupSearchParams, getTypeFilters, getGroupFilters } from '../../../utils/algoliaHelper';
import { search } from '../../../utils/requests/algoliaRequests';
import { TimedRecord, timedRecordGet, timedRecordSet } from '../../../utils/timedRecord';
import { DEFAULT_CACHE_EXPIRY_MS } from '../../config';
import { createAlert, updateAlert, deleteAlert } from '../../thunks/alerts';
import { fetchTrails } from '../../thunks/filters';

export const TRAIL_CACHE_EXPIRY_MS = DEFAULT_CACHE_EXPIRY_MS;
export const ALERT_CACHE_EXPIRY_MS = DEFAULT_CACHE_EXPIRY_MS;

type FetchArgs = GroupSearchParams & {
  draft?: boolean;
};

export type TrailMap = Record<TrailId, AlgoliaTrail>;
export type AlertTrailMap = Record<AlertId, TrailId[]>;

export type TrailsState = {
  draftTrails: TrailMap;
  draftAlertTrails: AlertTrailMap;
  trails: TrailMap;
  alertTrails: AlertTrailMap;
  started: boolean;
  loading: boolean;
  error: SerializedError;
  trailCache: Record<AlertId, TimedRecord<TrailId, AlgoliaTrail>>;
  alertCache: Record<TrailId, TimedRecord<AlertId, AlgoliaAlert>>;
};

export const initialState: TrailsState = {
  draftTrails: {},
  draftAlertTrails: {},
  trails: {},
  alertTrails: {},
  started: false,
  loading: false,
  error: null,
  trailCache: {},
  alertCache: {}
};

export const selectStatus = ({ trails: { started, loading, trails } }: { trails: TrailsState }) => ({ started, loading, trails });

export const fetchGroupsTrails = createAsyncThunk('trails/fetchGroupsTrails', async ({ indexId, groups, filters }: FetchArgs) => {
  // Prevent searching without any group_ids filter
  if (groups.length === 0) {
    return [];
  }

  const groupFilters = getGroupFilters(groups);
  const attributesToRetrieve = [
    'ID',
    'name',
    'slug',
    'length',
    'difficulty_rating',
    'avg_rating',
    'num_reviews',
    'area_name',
    'area_slug',
    'city_name',
    'state_name',
    'country_name',
    'alert_ids'
  ];

  if (filters) {
    filters = `${filters} AND ${groupFilters}`;
  } else {
    const typeFilters = getTypeFilters([AlgoliaObjectType.Trail]);
    filters = `${typeFilters} AND ${groupFilters}`;
  }

  const response = await search<AlgoliaTrail>(indexId, '', { attributesToRetrieve, filters, hitsPerPage: 1000 });
  return response.hits;
});

const addAlertIds = (state: Draft<TrailsState>, trailId: TrailId, alertId: AlertId) => {
  if (trailId in state.trails) {
    state.trails[trailId].alert_ids ||= [];
    const alertIds = state.trails[trailId].alert_ids;
    if (alertIds && !alertIds.includes(alertId)) {
      state.trails[trailId].alert_ids.push(alertId);
      state.alertTrails[alertId] ||= [];
      if (!state.alertTrails[alertId].includes(trailId)) {
        state.alertTrails[alertId].push(trailId);
      }
    }
  }
};

const removeAlertIds = (state: Draft<TrailsState>, alert: TrailsAlertResponse) => {
  (state.alertTrails[alert.id] || []).forEach(trailId => {
    if (!(alert.trailIds || []).includes(trailId)) {
      state.trails[trailId].alert_ids = state.trails[trailId].alert_ids.filter(alertId => alertId !== alert.id);
      state.alertTrails[alert.id] = state.alertTrails[alert.id].filter(id => id !== trailId);
    }
  });
};

const updateAlertIds = (state: Draft<TrailsState>, alert: TrailsAlertResponse) => {
  if (alert.type === AlertModel.AreaAlert) {
    (alert.trailIds || []).forEach(trailId => {
      addAlertIds(state, trailId, alert.id);
    });
    removeAlertIds(state, alert);
  } else if (alert.type === AlertModel.TrailAlert) {
    addAlertIds(state, alert.objectId, alert.id);
  }
};

// If trails were filtered by alertIds augment results with trails cached during creation of the alerts being filtered by
const addTrailsFromCache = (state: Draft<TrailsState>, trails: TrailMap, arg: FetchArgs) => {
  if (!arg.filters) {
    return;
  }

  const alertIds = [];
  const regex = /alert_ids:(\d+)/g;
  let matches;
  do {
    matches = regex.exec(arg.filters);
    if (matches && matches.length > 1) {
      alertIds.push(matches[1]);
    }
  } while (matches);

  alertIds.forEach((alertIdString: string) => {
    const alertId = parseInt(alertIdString);
    if (!(alertId in state.trailCache)) {
      return;
    }

    Object.keys(state.trailCache[alertId]).forEach(trailIdString => {
      const trailId = parseInt(trailIdString);
      const trail = timedRecordGet(state.trailCache[alertId], trailId, ALERT_CACHE_EXPIRY_MS);
      if (trail && !trails[trailId]) {
        trails[trailId] = trail;
      }
    });
  });
};

const addAlertsFromCache = (state: Draft<TrailsState>, trails: TrailMap, trail: AlgoliaTrail) => {
  if (!(trail.ID in state.alertCache)) {
    return;
  }

  trails[trail.ID].alert_ids ||= [];
  Object.keys(state.alertCache[trail.ID]).forEach(alertIdString => {
    const alertId = parseInt(alertIdString);
    const alert = timedRecordGet(state.alertCache[trail.ID], alertId, ALERT_CACHE_EXPIRY_MS);
    if (alert && !trails[trail.ID].alert_ids.includes(alertId)) {
      trails[trail.ID].alert_ids.push(alertId);
    }
  });
};

const updateCache = (state: Draft<TrailsState>, alert: TrailsAlertResponse) => {
  if (alert.type === AlertModel.AreaAlert) {
    (alert.trailIds || []).forEach(trailId => {
      const trail = state.trails[trailId];
      if (trail) {
        state.trailCache[alert.id] ||= {};
        timedRecordSet(state.trailCache[alert.id], trailId, trail);
      }
      state.alertCache[trailId] ||= {};
      timedRecordSet(state.alertCache[trailId], alert.id, getAlgoliaAlert(alert));
    });
  }
};

export const trailsSlice = createSlice({
  name: 'trails',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchTrails.pending, (state, action) => {
        state.draftTrails = {};
        state.draftAlertTrails = {};

        if (!action.meta.arg.draft) {
          state.started = true;
          state.loading = false;
          state.trails = {};
          state.alertTrails = {};
          state.error = null;
        }
      })
      .addCase(fetchGroupsTrails.pending, (state, action) => {
        state.draftTrails = {};
        state.draftAlertTrails = {};

        if (!action.meta.arg.draft) {
          state.started = true;
          state.loading = true;
          state.trails = {};
          state.alertTrails = {};
          state.error = null;
        }
      })
      .addCase(fetchGroupsTrails.fulfilled, (state, action) => {
        const trails: TrailMap = {};
        const alertTrails: AlertTrailMap = {};
        addTrailsFromCache(state, trails, action.meta.arg);
        action.payload.forEach(trail => {
          trails[trail.ID] = trail;
        });

        // Build a map of alertId to trailIds so we can detect when an alert has been removed from a trail
        Object.keys(trails).forEach(trailIdString => {
          const trailId = parseInt(trailIdString);
          const trail = trails[trailId];
          addAlertsFromCache(state, trails, trail);
          (trail.alert_ids || []).forEach(alertId => {
            alertTrails[alertId] ||= [];
            alertTrails[alertId].push(trail.ID);
          });
        });

        if (action.meta.arg.draft) {
          state.draftTrails = trails;
          state.draftAlertTrails = alertTrails;
        } else {
          state.loading = false;
          state.trails = trails;
          state.alertTrails = alertTrails;
        }
      })
      .addCase(fetchGroupsTrails.rejected, (state, action) => {
        if (action.meta.arg.draft) {
          return;
        }

        state.loading = false;
        state.error = action.error;
      })
      .addCase(createAlert.fulfilled, (state, action) => {
        action.payload.forEach(alert => {
          updateAlertIds(state, alert);
          updateCache(state, alert);
        });
      })
      .addCase(updateAlert.fulfilled, (state, action) => {
        updateAlertIds(state, action.payload);
        updateCache(state, action.payload);
      })
      .addCase(deleteAlert.fulfilled, (state, action) => {
        removeAlertIds(state, action.payload);
      });
  }
});

export default trailsSlice.reducer;
